import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiArrowLeft,
  FiSave,
  FiPlay,
  FiSettings,
  FiMic,
  FiMessageCircle,
  FiBook,
  FiPhone,
  FiTool,
  FiGlobe,
  FiUser,
  FiTarget,
  FiThermometer,
  FiHash,
  FiEdit3
} from 'react-icons/fi';
import { api } from '../utils/api';
import toast from 'react-hot-toast';
import { VoiceLabPanel } from './AssistantBuilder/components/VoiceTab/VoiceLabPanel';
import { ChatLabPanel } from './AssistantBuilder/components/ChatTab/ChatLabPanel';
import { KnowledgeLabPanel } from './AssistantBuilder/components/KnowledgeTab/KnowledgeLabPanel';

interface Assistant {
  id: string;
  name: string;
  description: string;
  systemPrompt: string;
  model: string;
  temperature: number;
  maxTokens: number;
  voiceEnabled: boolean;
  voiceProvider?: string;
  voiceId?: string;
  voiceSettings?: any;
  createdAt: string;
  updatedAt: string;
}

const AssistantBuilder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [assistant, setAssistant] = useState<Assistant | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('builder');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    systemPrompt: '',
    model: 'gpt-4-turbo-preview',
    temperature: 0.7,
    maxTokens: 1000,
    voiceEnabled: false,
    voiceProvider: '',
    voiceId: '',
    voiceSettings: {}
  });

  useEffect(() => {
    if (id) {
      fetchAssistant();
    }
  }, [id]);

  const fetchAssistant = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/assistants/${id}`);
      const assistantData = response.data.assistant;
      setAssistant(assistantData);
      
      // Populate form data
      setFormData({
        name: assistantData.name || '',
        description: assistantData.description || '',
        systemPrompt: assistantData.systemPrompt || '',
        model: assistantData.model || 'gpt-4-turbo-preview',
        temperature: assistantData.temperature || 0.7,
        maxTokens: assistantData.maxTokens || 1000,
        voiceEnabled: assistantData.voiceEnabled || false,
        voiceProvider: assistantData.voiceProvider || '',
        voiceId: assistantData.voiceId || '',
        voiceSettings: assistantData.voiceSettings || {}
      });
    } catch (error) {
      console.error('Failed to fetch assistant:', error);
      toast.error('Failed to load assistant');
      navigate('/assistants');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put(`/assistants/${id}`, formData);
      toast.success('Assistant saved successfully!');
      await fetchAssistant(); // Refresh data
    } catch (error) {
      console.error('Failed to save assistant:', error);
      toast.error('Failed to save assistant');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = React.useCallback((field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleVoiceChange = React.useCallback((voice: any) => {
    handleInputChange('voiceId', voice.id);
    handleInputChange('voiceProvider', voice.provider);
  }, [handleInputChange]);

  const sidebarSections = [
    { id: 'builder', name: 'Builder', icon: FiUser, active: true },
    { id: 'voice', name: 'Voice Lab', icon: FiMic },
    { id: 'chat', name: 'Chat Lab', icon: FiMessageCircle },
    { id: 'knowledge', name: 'Knowledge Lab', icon: FiBook },
  ];

  const rightSidebarSections = [
    { id: 'chat-settings', name: 'Chat Settings', icon: FiSettings },
    { id: 'call-settings', name: 'Call Settings', icon: FiPhone },
    { id: 'tools', name: 'Tools & APIs', icon: FiTool },
    { id: 'integrations', name: 'Integrations', icon: FiGlobe },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!assistant) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="text-center p-8 rounded-2xl" style={{
          background: 'rgba(30, 41, 59, 0.8)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-4">
            Assistant not found
          </h2>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/assistants')}
            className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg transition-colors font-medium"
          >
            Back to Assistants
          </motion.button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.1, 0.3, 0.1],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 18 + i * 2,
              repeat: Infinity,
              delay: i * 0.5,
            }}
          >
            <FiUser size={30 + i * 5} className="text-purple-500/20" />
          </motion.div>
        ))}
      </div>

      {/* Left Sidebar */}
      <div className="w-64 flex flex-col relative z-10" style={{
        background: 'rgba(30, 41, 59, 0.8)',
        backdropFilter: 'blur(20px)',
        borderRight: '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        {/* Header */}
        <div className="p-4" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <div className="flex items-center gap-3 mb-4">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => navigate('/assistants')}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <FiArrowLeft size={20} />
            </motion.button>
            <h1 className="text-lg font-semibold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent truncate">
              {assistant.name}
            </h1>
          </div>
          <div className="flex gap-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 disabled:opacity-50 text-white rounded-lg transition-colors flex items-center justify-center gap-2 text-sm font-medium"
            >
              <FiSave size={16} />
              {saving ? 'Saving...' : 'Save'}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
            >
              <FiPlay size={16} />
              Test
            </motion.button>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex-1 p-4">
          <nav className="space-y-2">
            {sidebarSections.map((section) => (
              <motion.button
                key={section.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left ${
                  activeSection === section.id
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                    : 'text-gray-300 hover:text-white hover:bg-gray-800/50'
                }`}
              >
                <section.icon size={20} />
                {section.name}
              </motion.button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden relative z-10">
        {/* Content Area */}
        <div className="flex-1 overflow-auto p-6">
          {activeSection === 'builder' && (
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Assistant Name */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-6 rounded-2xl"
                style={{
                  background: 'rgba(30, 41, 59, 0.8)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  <div className="flex items-center gap-2">
                    <FiUser size={16} className="text-purple-400" />
                    Assistant Name
                  </div>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 transition-colors text-white placeholder-gray-400"
                  placeholder="Enter assistant name"
                />
              </motion.div>

              {/* Description */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="p-6 rounded-2xl"
                style={{
                  background: 'rgba(30, 41, 59, 0.8)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  <div className="flex items-center gap-2">
                    <FiTarget size={16} className="text-blue-400" />
                    Description
                  </div>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 transition-colors text-white placeholder-gray-400 h-24 resize-none"
                  placeholder="Brief description of your assistant"
                />
              </motion.div>

              {/* System Prompt */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="p-6 rounded-2xl"
                style={{
                  background: 'rgba(30, 41, 59, 0.8)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  <div className="flex items-center gap-2">
                    <FiEdit3 size={16} className="text-green-400" />
                    System Prompt
                  </div>
                </label>
                <textarea
                  value={formData.systemPrompt}
                  onChange={(e) => handleInputChange('systemPrompt', e.target.value)}
                  className="w-full px-4 py-4 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 transition-colors text-white placeholder-gray-400 font-mono text-sm leading-relaxed"
                  placeholder="Enter your system prompt..."
                  rows={20}
                />
              </motion.div>
            </div>
          )}

          {activeSection === 'voice' && assistant && (
            <div className="h-full">
              <VoiceLabPanel
                assistantId={assistant.id}
                onVoiceChange={handleVoiceChange}
              />
            </div>
          )}

          {activeSection === 'chat' && assistant && (
            <div className="h-full">
              <ChatLabPanel
                assistant={assistant as any}
                globalPrompt={assistant.systemPrompt}
                onMemoryUpdate={(hasMemory) => {
                  console.log('Memory status:', hasMemory);
                }}
              />
            </div>
          )}

          {activeSection === 'knowledge' && assistant && (
            <div className="h-full">
              <KnowledgeLabPanel
                assistantId={assistant.id}
                onDocumentsChange={(count) => {
                  console.log('Document count:', count);
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Right Sidebar */}
      <div className="w-80 p-4 relative z-10" style={{
        background: 'rgba(30, 41, 59, 0.8)',
        backdropFilter: 'blur(20px)',
        borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        <h3 className="text-lg font-semibold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-4">Configuration</h3>
        
        <div className="space-y-6">
          {/* Model Settings */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-300 uppercase tracking-wide">Model Settings</h4>
            
            {/* Model */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Model</label>
              <select
                value={formData.model}
                onChange={(e) => handleInputChange('model', e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 transition-colors text-white text-sm"
              >
                <option value="gpt-4-turbo-preview">GPT-4 Turbo</option>
                <option value="gpt-4">GPT-4</option>
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
              </select>
            </div>

            {/* Temperature */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <div className="flex items-center gap-2">
                  <FiThermometer size={14} className="text-orange-400" />
                  Temperature: {formData.temperature}
                </div>
              </label>
              <input
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={formData.temperature}
                onChange={(e) => handleInputChange('temperature', parseFloat(e.target.value))}
                className="w-full accent-purple-500"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Precise</span>
                <span>Creative</span>
              </div>
            </div>

            {/* Max Tokens */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <div className="flex items-center gap-2">
                  <FiHash size={14} className="text-cyan-400" />
                  Max Tokens
                </div>
              </label>
              <input
                type="number"
                min="1"
                max="4000"
                value={formData.maxTokens}
                onChange={(e) => handleInputChange('maxTokens', parseInt(e.target.value))}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 transition-colors text-white text-sm"
              />
            </div>
          </div>

          {/* Voice Settings */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-300 uppercase tracking-wide">Voice Settings</h4>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-300">Enable Voice</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.voiceEnabled}
                  onChange={(e) => handleInputChange('voiceEnabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-purple-500 peer-checked:to-pink-500"></div>
              </label>
            </div>

            {formData.voiceEnabled && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Voice Provider</label>
                  <select
                    value={formData.voiceProvider}
                    onChange={(e) => handleInputChange('voiceProvider', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 transition-colors text-white text-sm"
                  >
                    <option value="">Select Provider</option>
                    <option value="elevenlabs">ElevenLabs</option>
                    <option value="openai">OpenAI</option>
                    <option value="azure">Azure</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Voice ID</label>
                  <input
                    type="text"
                    value={formData.voiceId}
                    onChange={(e) => handleInputChange('voiceId', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 transition-colors text-white text-sm"
                    placeholder="Enter voice ID"
                  />
                </div>
              </>
            )}
          </div>

          {/* Quick Actions */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-300 uppercase tracking-wide">Quick Actions</h4>
            {rightSidebarSections.map((section) => (
              <motion.button
                key={section.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors text-sm"
              >
                <section.icon size={16} />
                {section.name}
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssistantBuilder;